import { Skeleton } from "@/components/ui/skeleton";
import { useGetTargetareContacteStats } from "@/hooks/api/query/useGetTargetareContacteStats";
import { useGetTargetareTermeneStats } from "@/hooks/api/query/useGetTargetareTermeneStats";
import { formatDataOra, formatNumar } from "@/utils/targetareFormatting";
import { HeroPagina } from "@/pages/AdministrareTargetare/components/HeroPagina";

export default function AlteSurse() {
  const termene = useGetTargetareTermeneStats();
  const contacte = useGetTargetareContacteStats();
  const t = termene.data;
  const c = contacte.data;

  return (
    <div className="space-y-6">
      <HeroPagina
        titlu="Alte surse"
        descriere={
          <>
            Surse fără snapshot al lor: interogările Termene.ro (cotă limitată
            până la expirarea licenței pe 20.09.2026) și descoperirea automată
            de contacte pe site-ul firmei. Ambele scriu direct în tabelele de
            firme/persoane/contacte, fără o etapă de aplicare separată — aici
            sunt doar de citire.
          </>
        }
        isLoading={termene.isLoading}
        coloane={4}
        tiles={[
          { eticheta: "Răspunsuri Termene", valoare: t?.raspunsuri },
          { eticheta: "Cu extras", valoare: t?.cu_extras, ton: "bun" },
          {
            eticheta: "Fără extras",
            valoare: t?.fara_extras,
            ton: (t?.fara_extras ?? 0) > 0 ? "atentie" : "implicit",
          },
          {
            eticheta: "Ultima interogare",
            valoare: t?.ultima_interogare
              ? formatDataOra(t.ultima_interogare)
              : "–",
          },
        ]}
      />

      {(termene.error || contacte.error) && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {termene.error?.message ?? contacte.error?.message}
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-md border">
          <p className="border-b px-3 py-2 text-sm font-medium">
            Coada Termene (rămase de interogat)
          </p>
          {termene.isLoading ? (
            <div className="space-y-2 p-3">
              <Skeleton className="h-4 w-full" />
            </div>
          ) : Object.keys(t?.coada ?? {}).length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">Coada e goală.</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {Object.entries(t?.coada ?? {}).map(([stare, n]) => (
                  <tr key={stare} className="border-t">
                    <td className="px-3 py-1.5 capitalize">
                      {stare.split("_").join(" ")}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {formatNumar(n)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="rounded-md border">
          <p className="border-b px-3 py-2 text-sm font-medium">
            Descoperire web (contacte)
          </p>
          {contacte.isLoading ? (
            <div className="space-y-2 p-3">
              <Skeleton className="h-4 w-full" />
            </div>
          ) : (
            <div className="space-y-2 p-3 text-sm">
              <table className="w-full">
                <tbody>
                  {Object.entries(c?.descoperire ?? {}).map(([stare, n]) => (
                    <tr key={stare} className="border-t first:border-t-0">
                      <td className="py-1 capitalize">
                        {stare.split("_").join(" ")}
                      </td>
                      <td className="py-1 text-right tabular-nums">
                        {formatNumar(n)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-muted-foreground">
                {formatNumar(c?.descoperire_site_verificat ?? 0)} site-uri
                verificate · ultima rulare{" "}
                {c?.descoperire_ultima
                  ? formatDataOra(c.descoperire_ultima)
                  : "–"}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-md border">
        <p className="border-b px-3 py-2 text-sm font-medium">
          Ultimele joburi de extracție Termene
        </p>
        {termene.isLoading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-4 w-full" />
          </div>
        ) : (t?.joburi_recente ?? []).length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">
            Niciun job de extracție rulat încă.
          </p>
        ) : (
          <ul className="divide-y text-sm">
            {(t?.joburi_recente ?? []).map((j) => (
              <li key={j.id} className="space-y-0.5 px-3 py-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs">
                    #{j.id} · {j.stare}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDataOra(j.inceput_la)}
                    {j.sfarsit_la ? ` → ${formatDataOra(j.sfarsit_la)}` : ""}
                  </span>
                </div>
                {j.jurnal && (
                  <p className="text-xs text-muted-foreground">{j.jurnal}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
